import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { IMAGES } from "@/data/content";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, lang, toggle } = useLanguage();

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/a-propos", label: t.nav.about },
    { to: "/offres", label: t.nav.jobs },
    { to: "/contact", label: t.nav.contact },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-y5-ink/90 backdrop-blur-xl border-b border-y5-line" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center">
          <div className="h-12 w-12 overflow-hidden flex items-center justify-center">
            <img src={IMAGES.sigle} alt="Yellow5" className="w-full h-full object-cover scale-[1.9]" />
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to}`}
              className={`text-sm tracking-wide transition-colors hover:text-y5-yellow ${
                location.pathname === l.to ? "text-y5-yellow" : "text-white/70"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className="flex items-center gap-1.5 text-sm text-white/70 hover:text-y5-yellow transition-colors border-l border-y5-line pl-6"
          >
            <Globe size={15} /> {lang === "en" ? "FR" : "EN"}
          </button>
          <button
            data-testid="nav-offres-cta"
            onClick={() => navigate("/offres")}
            className="group relative overflow-hidden bg-y5-yellow text-y5-ink px-6 py-2.5 text-sm font-semibold tracking-wide transition-transform hover:scale-[1.03]"
          >
            {t.nav.cta}
          </button>
        </nav>

        <div className="md:hidden flex items-center gap-4">
          <button onClick={toggle} data-testid="lang-toggle-mobile" className="flex items-center gap-1 text-sm text-white/80">
            <Globe size={16} /> {lang === "en" ? "FR" : "EN"}
          </button>
          <button className="text-white" data-testid="mobile-menu-toggle" onClick={() => setOpen((o) => !o)}>
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-y5-ink border-t border-y5-line"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {links.map((l) => (
                <Link key={l.to} to={l.to} className="text-lg text-white/80 hover:text-y5-yellow">
                  {l.label}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
